import { useEffect, useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Alert } from '@mui/material';
import { DEFAULT_ADVANCE_BUTTON_STYLE, DEFAULT_CANCEL_BUTTON_STYLE } from '../../../utils/styleHelpers';
import { McpAccessProfile, McpAccessScope } from '../../../store/McpAccess';
import { McpAccessFields } from './McpAccessFields';

interface McpAccessEditDialogProps {
  open: boolean;
  profile: McpAccessProfile | null;
  allowedScopes: McpAccessScope[];
  onClose: () => void;
  onSave: (id: string, update: { title: string; scope: McpAccessScope }) => void;
  submitting?: boolean;
}

export const McpAccessEditDialog = ({
  open,
  profile,
  allowedScopes,
  onClose,
  onSave,
  submitting = false
}: McpAccessEditDialogProps) => {
  const [title, setTitle] = useState('');
  const [scope, setScope] = useState<McpAccessScope>('read_only');

  useEffect(() => {
    if (profile) {
      setTitle(profile.title);
      setScope(profile.scope);
    }
  }, [profile]);

  if (!profile) return null;

  const canSave = title.trim().length > 0 && !submitting;

  const handleSave = () => {
    if (!canSave) return;
    onSave(profile.id, { title: title.trim(), scope });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="mcp-edit-title">
      <DialogTitle id="mcp-edit-title">Edit token</DialogTitle>
      <DialogContent>
        <McpAccessFields
          title={title}
          scope={scope}
          allowedScopes={allowedScopes}
          onTitleChange={setTitle}
          onScopeChange={setScope}
        />
        {scope !== profile.scope && (
          <Alert
            severity="info"
            sx={{ mt: 2 }}>
            Changing the scope issues a new token and immediately invalidates the current one.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <button
          type="button"
          className={DEFAULT_CANCEL_BUTTON_STYLE}
          disabled={submitting}
          onClick={onClose}>
          Cancel
        </button>
        <button
          type="button"
          className={DEFAULT_ADVANCE_BUTTON_STYLE}
          disabled={!canSave}
          onClick={handleSave}>
          Save
        </button>
      </DialogActions>
    </Dialog>
  );
};
